import { createClient } from '@connectrpc/connect';
import { RevitService } from './gen/revit_service_pb';
import { getGrpcTransport, resetGrpcTransport } from './transport';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { perf } from '../../lib/perfLogger';
import {
  mapOperationProgressSnapshot,
  type RevitOperationProgressSnapshot,
} from './operationProgressSnapshotMapper';
import {
  pollRevitOperationWithTransport,
  type RevitOperationPollOptions,
} from './operationProgressPoller';
import { RevitProgressRequestTimeoutMs } from '../../lib/revit/revitOperationTimeoutPolicy';

export { mapOperationProgressSnapshot } from './operationProgressSnapshotMapper';
export { resolveSnapshotProgressVisibility } from './operationProgressVisibility';
export type {
  RevitOperationLifecycleState,
  RevitOperationProgressItem,
  RevitOperationProgressItemStatus,
  RevitOperationProgressSnapshot,
} from './operationProgressSnapshotMapper';
export type { RevitOperationPollOptions } from './operationProgressPoller';

// Registry of in-flight backend operations. The Revit-side operation keeps
// running even if the WebView2 renderer crashes or the page reloads, so the
// pending id lets the UI re-attach and resume polling instead of fossilizing
// a stale progress window.
const PENDING_OPERATION_STORAGE_KEY = 'pipeex:pendingRevitOperation';

export interface PendingConnectorTarget {
  destination: 'project' | 'family';
  bindingKind: 'type' | 'instance';
}

export interface PendingRevitOperationV2 {
  version: 2;
  operationId: string;
  operationType: string;
  owner: string;
  title: string;
  startedAt: number;
  projectKey: string;
  expectedDocumentName: string | null;
  expectedDocumentPath: string | null;
  connectorTarget: PendingConnectorTarget | null;
}

/**
 * Caller-supplied provenance for a pending-record write. The generic family
 * client never invents owner/title/context on its own; it only appends the
 * mechanical version/operationId/operationType/startedAt fields once the
 * backend has returned an operation id.
 */
export type PendingRevitOperationRecoveryMetadata = Omit<
  PendingRevitOperationV2,
  'version' | 'operationId' | 'operationType' | 'startedAt'
>;

export type PendingRevitOperationRead =
  | { kind: 'none' }
  | { kind: 'valid'; record: PendingRevitOperationV2 }
  | { kind: 'legacy'; operationId: string; operationType: string }
  | { kind: 'malformed'; operationId: string | null };

const ConnectorParametersOwner = 'connector-parameters';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const parseConnectorTarget = (value: unknown): PendingConnectorTarget | null => {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const target = value as Partial<PendingConnectorTarget>;
  return (target.destination === 'project' || target.destination === 'family') &&
      (target.bindingKind === 'type' || target.bindingKind === 'instance')
    ? { destination: target.destination, bindingKind: target.bindingKind }
    : null;
};

const toIdentityPart = (value: unknown): string | null =>
  isNonEmptyString(value) ? value : null;

function classifyStoredOperation(raw: string): PendingRevitOperationRead {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { kind: 'malformed', operationId: null };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { kind: 'malformed', operationId: null };
  }

  const candidate = parsed as Record<string, unknown>;
  const operationId = isNonEmptyString(candidate.operationId)
    ? candidate.operationId
    : null;

  if (!('version' in candidate)) {
    return operationId
      ? {
          kind: 'legacy',
          operationId,
          operationType: typeof candidate.operationType === 'string'
            ? candidate.operationType
            : '',
        }
      : { kind: 'malformed', operationId: null };
  }

  if (candidate.version !== 2 || !operationId) {
    return { kind: 'malformed', operationId };
  }

  const connectorTarget = parseConnectorTarget(candidate.connectorTarget);
  const expectedDocumentName = toIdentityPart(candidate.expectedDocumentName);
  const expectedDocumentPath = toIdentityPart(candidate.expectedDocumentPath);
  const hasValidShape =
    isNonEmptyString(candidate.operationType) &&
    isNonEmptyString(candidate.owner) &&
    isNonEmptyString(candidate.projectKey) &&
    typeof candidate.title === 'string' &&
    typeof candidate.startedAt === 'number' &&
    (expectedDocumentName !== null || expectedDocumentPath !== null) &&
    (candidate.owner === ConnectorParametersOwner) === (connectorTarget !== null);

  if (!hasValidShape) {
    return { kind: 'malformed', operationId };
  }

  return {
    kind: 'valid',
    record: {
      version: 2,
      operationId,
      operationType: candidate.operationType as string,
      owner: candidate.owner as string,
      title: candidate.title as string,
      startedAt: candidate.startedAt as number,
      projectKey: candidate.projectKey as string,
      expectedDocumentName,
      expectedDocumentPath,
      connectorTarget,
    },
  };
}

export function setPendingRevitOperation(record: PendingRevitOperationV2): void {
  try {
    window.sessionStorage?.setItem(PENDING_OPERATION_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // storage unavailable (privacy mode) — progress recovery is best-effort
  }
}

export function readPendingRevitOperation(): PendingRevitOperationRead {
  try {
    const raw = window.sessionStorage?.getItem(PENDING_OPERATION_STORAGE_KEY);
    if (!raw) {
      return { kind: 'none' };
    }

    return classifyStoredOperation(raw);
  } catch {
    return { kind: 'none' };
  }
}

/**
 * The operation a stored record names, whatever shape the record has. A
 * malformed record still names its operation whenever it carries a usable
 * `operationId`, so ownership checks must see that id too — otherwise a stale
 * caller clearing an older id would drop a newer record it does not own.
 */
function storedOperationIdOf(read: PendingRevitOperationRead): string | null {
  switch (read.kind) {
    case 'valid':
      return read.record.operationId;
    case 'legacy':
      return read.operationId;
    case 'malformed':
      return read.operationId;
    default:
      return null;
  }
}

export function clearPendingRevitOperation(operationId?: string): void {
  try {
    if (operationId) {
      const storedId = storedOperationIdOf(readPendingRevitOperation());
      if (storedId && storedId !== operationId) {
        return;
      }
    }

    window.sessionStorage?.removeItem(PENDING_OPERATION_STORAGE_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
}

function getClient() {
  const transport = getGrpcTransport();
  if (!transport) {
    return null;
  }

  return createClient(RevitService, transport);
}

const MaxLoggedItems = 3;
const MaxLoggedItemLabelLength = 64;

const sanitizeLoggedItemLabel = (value: string): string =>
  value.replace(/[|,\r\n]/g, '_').slice(0, MaxLoggedItemLabelLength);

export const formatOperationProgressForLog = (
  progress: RevitOperationProgressSnapshot
): string => {
  const itemStateCounts = new Map<string, number>();
  progress.items.forEach((item) => {
    itemStateCounts.set(item.status, (itemStateCounts.get(item.status) ?? 0) + 1);
  });
  const itemStates = Array.from(itemStateCounts.entries())
    .map(([status, count]) => `${status}:${count}`)
    .join('|');
  const itemSample = progress.items
    .slice(0, MaxLoggedItems)
    .map((item) => `${sanitizeLoggedItemLabel(item.label)}:${item.status}`)
    .join('|');
  const fields = [
    `operationId=${progress.operationId}`,
    `operationType=${progress.operationType}`,
    `phase=${progress.phase}`,
    `state=${progress.state ?? 'unspecified'}`,
    `message=${progress.message}`,
    `processed=${progress.processed ?? ''}`,
    `total=${progress.total ?? ''}`,
    `percent=${progress.percent ?? ''}`,
    `resultReady=${progress.resultReady}`,
    `failed=${progress.failed}`,
    `elapsedMs=${progress.elapsedMs ?? ''}`,
    `activeElapsedMs=${progress.activeElapsedMs ?? ''}`,
    `waitElapsedMs=${progress.waitElapsedMs ?? ''}`,
    `etaMs=${progress.etaMs ?? ''}`,
    `waitReason=${progress.waitReason ?? ''}`,
    `currentItem=${progress.currentItem ?? ''}`,
    `itemCount=${progress.items.length}`,
    `itemStates=${itemStates}`,
    `itemSample=${itemSample}`,
    `detail=${progress.detail ?? ''}`,
    `error=${progress.error ?? ''}`,
  ];

  return fields.join(', ');
};

export const getRevitAcceptanceTimeoutError = (
  progress: RevitOperationProgressSnapshot,
  maxRevitWaitMs?: number
): string | undefined => {
  if (
    !maxRevitWaitMs ||
    maxRevitWaitMs <= 0 ||
    progress.state !== 'waiting_for_revit' ||
    (progress.waitElapsedMs ?? 0) < maxRevitWaitMs
  ) {
    return undefined;
  }

  const waitSeconds = Math.max(1, Math.round(maxRevitWaitMs / 1000));
  return `Revit did not accept the operation within ${waitSeconds}s. Finish the active Revit command or close any open dialog, then try again.`;
};

export async function getOperationProgressGrpc(
  operationId: string,
  requestTimeoutMs = RevitProgressRequestTimeoutMs
): Promise<{ success: boolean; progress?: RevitOperationProgressSnapshot; error?: string }> {
  const client = getClient();
  if (!client) {
    const message = 'GetOperationProgress: gRPC transport is unavailable';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(message);
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const tProgressRequest = performance.now();
    const response = await client.getOperationProgress(
      { operationId },
      { signal: AbortSignal.timeout(requestTimeoutMs) }
    );
    perf.slow('RevitOperation', 'getOperationProgress await', tProgressRequest, 100, {
      operationId,
      success: response.isSuccess,
      hasProgress: Boolean(response.progress),
    });
    if (!response.isSuccess || !response.progress) {
      return { success: false, error: response.error || 'Operation progress was not returned' };
    }

    const tProgressMap = performance.now();
    const progress = mapOperationProgressSnapshot(response.progress);
    perf.slow('RevitOperation', 'map operation progress', tProgressMap, 25, {
      operationId,
      phase: progress.phase,
      state: progress.state,
      processed: progress.processed,
      total: progress.total,
      resultReady: progress.resultReady,
    });
    return { success: true, progress };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetOperationProgress gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetOperationProgress: ${message}`);
    return { success: false, error: message };
  }
}

export async function getOperationResultGrpc(
  operationId: string
): Promise<{ success: boolean; resultReady?: boolean; resultJson?: string; error?: string }> {
  const client = getClient();
  if (!client) {
    const message = 'GetOperationResult: gRPC transport is unavailable';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(message);
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const tResultRequest = performance.now();
    const response = await client.getOperationResult({ operationId });
    perf.slow('RevitOperation', 'getOperationResult await', tResultRequest, 100, {
      operationId,
      success: response.isSuccess,
      resultReady: response.resultReady,
      resultJsonLength: response.resultJson?.length ?? 0,
    });
    return {
      success: response.isSuccess,
      resultReady: response.resultReady,
      resultJson: response.resultJson || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetOperationResult gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetOperationResult: ${message}`);
    return { success: false, error: message };
  }
}

export async function pollRevitOperation<T>(
  operationId: string,
  options: RevitOperationPollOptions<T>
): Promise<{ success: boolean; result?: T; error?: string }> {
  return pollRevitOperationWithTransport(operationId, options, {
    getProgress: getOperationProgressGrpc,
    getResult: getOperationResultGrpc,
    resetTransport: resetGrpcTransport,
    formatProgress: formatOperationProgressForLog,
    getAcceptanceTimeoutError: getRevitAcceptanceTimeoutError,
  });
}
